import ChatHelper from '@/shared/util/chat';
import Environment from '@/shared/util/environment';
import LaunchDarkly from '@/shared/vendor/launchDarkly';
import { posthogIdentify, setPosthogSuperProperties } from '@/shared/vendor/posthog';
import ElevioHelper from '@/shared/vendor/util/elevio';
import GoogleTagManagerHelper from '@/shared/vendor/util/googleTagManager';
import { identifyVitallyNps } from '@/shared/vendor/vitallyNps';

import { LicenseManager } from '@ag-grid-enterprise/core';
import { loadStripe } from '@stripe/stripe-js';
import { client as Elevio } from 'elevio';
import { get, isFunction } from 'lodash';

export default class Identify {
  /**
   *
   * @param {User} user
   * @param {Account} account
   * @param {Plan} plan
   * @param {Login} login
   * @param dispatch
   */
  static identifyThirdParty(user, account, plan, login, dispatch) {
    this.identifyLaunchDarkly(user, account, plan, dispatch);
    this.identifyElevio(user, account, plan);
    // Zendesk waits for the migration status to resolve in ZendeskMessengerInitializer.
    this.identifyMercury(user);
    this.identifyGoogleTagManager(user, account, plan);
    this.identifyAgGrid();
    this.identifyPosthog(user, account, login, plan);
    this.identifyVitallyNps(user, account, plan);
    this.identifyStripe(account);
  }

  static identifyLaunchDarkly(user, account, plan, dispatch) {
    try {
      LaunchDarkly.init(user, account, plan, dispatch);
    } catch (e) {
      console.warn('Launch Darkly identification issue: ', e);
    }
  }

  static identifyMercury(user) {
    try {
      if (typeof window.mercury !== 'undefined') {
        //Mercury expects 'development' rather than 'dev'
        const env = Environment.get() === 'dev' ? 'development' : Environment.get();
        window.mercury.identify(user.account_id, user.id, user.login_id, 'wheniwork-js', env);
      }
    } catch (e) {
      console.warn('Mercury identification issue: ', e);
    }
  }

  static identifyGoogleTagManager(user, account, plan) {
    try {
      // No need to wait for GTM async library to load.
      // GTM gracefully handles data being pushed to data layer before library is loaded
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        user: GoogleTagManagerHelper.userData(user),
        account: GoogleTagManagerHelper.accountData(account, plan),
      });
      window.dataLayer.push({ event: 'userAttributesSet' });
    } catch (e) {
      console.warn('Google Tag Manager identification issue: ', e);
    }
  }

  static identifyElevio(user, account, plan) {
    try {
      // require elevio only after we've booted the app and have sufficient data about the user.
      // and after we've waited a bit. elevio is slow.
      setTimeout(() => {
        Elevio.load('56cc6a1ee2ffa').then(() => {
          if (isFunction(get(window, '_elev.changeUser'))) {
            // Prevent spurious console errors when domain is blocked (e.g., NoScript users)
            window._elev.changeUser(ElevioHelper.identifyUser(user));
          }
          if (isFunction(get(window, '_elev.disableModules'))) {
            // Prevent spurious console errors when domain is blocked (e.g., NoScript users)
            if (window.zE?.widget === 'messenger') {
              window._elev.disableModules(ElevioHelper.getDisabledModules(user, account, plan, true));
            } else {
              window._elev.disableModules(ElevioHelper.getDisabledModules(user, account, plan));
            }
          }
          if (isFunction(get(window, '_elev.on'))) {
            window._elev.on('module:opened', function (moduleId) {
              if (moduleId === 4) {
                window.zE?.('webWidget', 'show');
                window.zE?.('webWidget', 'open');
              }
              if (moduleId === 19) {
                window.zE?.('messenger', 'show');
                window.zE?.('messenger', 'open');
              }
            });
          }
        });
      }, 5000);
    } catch (e) {
      console.warn('Elevio identification issue: ', e);
    }
  }

  static identifyZendesk(user, account, plan) {
    try {
      if (ChatHelper.canChat(user, plan)) {
        import('./zendesk').then(zendesk => {
          zendesk.initializeZendeskMessengerWidget(user, account, plan);
        });
      }
    } catch (e) {
      console.warn('Zendesk identification issue: ', e);
    }
  }

  static identifyAgGrid() {
    try {
      LicenseManager.setLicenseKey(CONFIG.AG_GRID_LICENSE_KEY);
    } catch (e) {
      console.warn('AG Grid identification issue: ', e);
    }
  }

  static identifyPosthog(user, account, login, plan) {
    try {
      posthogIdentify(user.id, {
        Name: user.fullName,
        Email: user.email,
        'Account ID': account.id,
        'Person ID': login.id,
        'User role': user.roleName,
        'Plan ID': plan.id,
        Environment: Environment.get(),
      });
      setPosthogSuperProperties(user, account, login);
    } catch (e) {
      console.warn('Posthog identification issue: ', e);
    }
  }

  static identifyVitallyNps(user, account, plan) {
    try {
      identifyVitallyNps(user, account, plan);
    } catch (e) {
      console.warn('Vitally NPS identification issue: ', e);
    }
  }

  // TODO: remove this when all components are moved to useWiwStripe hook
  static async identifyStripe(account) {
    try {
      const stripeKey = account.isV5Billing() ? CONFIG.STRIPE.KEY : CONFIG.STRIPE_KEY;
      window.stripe = await loadStripe(stripeKey);
    } catch (e) {
      console.warn('Stripe identification issue: ', e);
    }
  }
}
